import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@apollo/client'
import { CircularProgress } from '@mui/material'

import { Acknowledge, Button, CardBox, Fields, Row, Stack, Text } from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'

export function AcceptSuccess() {
  return (
    <Acknowledge.Success
      title="Connection Accepted"
      content="You now have view access to this donor's giving fund. You can manage their securities transfer from your advisor dashboard."
    >
      <Button to="/funds">Go to Advisor Dashboard</Button>
    </Acknowledge.Success>
  )
}

export function AcceptError({ message }) {
  const isAlreadyAccepted = message && message.includes('already been accepted')
  return (
    <Acknowledge.Error
      title={isAlreadyAccepted ? 'Connection Already Accepted' : 'Unable to Accept Connection'}
      content={
        isAlreadyAccepted
          ? 'This connection has already been accepted. You can access the fund from your advisor dashboard.'
          : message || 'We were unable to process your request. The link may be invalid or expired.'
      }
    >
      <Button to={isAlreadyAccepted ? '/funds' : '/login'}>
        {isAlreadyAccepted ? 'Go to Advisor Dashboard' : 'Go to Login'}
      </Button>
    </Acknowledge.Error>
  )
}

export function AcceptLoading() {
  return (
    <Stack alignItems="center" spacing={2} sx={{ mt: 8 }}>
      <CircularProgress />
      <span>Processing your connection request...</span>
    </Stack>
  )
}

function normalizeForNameMatch(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function needsNameCollection(me) {
  if (!me) return false
  const firstName = me.profile?.firstName?.trim() || ''
  const lastName = me.profile?.lastName?.trim() || ''
  if (!firstName || !lastName) return true

  // Charity portal users are created with a placeholder profile name derived from the
  // charity's account name and id (api/src/management/manage-charities.service.ts) - the
  // whole account name can land in firstName with the charityId in lastName, or the account
  // name itself can be split word-by-word across both fields with a charity number appended.
  // Normalizing and comparing the combined name catches either shape.
  const normalizedFullName = normalizeForNameMatch(`${firstName}${lastName}`)

  return (me.charityUsers || []).some((cu) => {
    const normalizedAccountName = normalizeForNameMatch(cu.charity?.accountName)
    const normalizedCharityId = normalizeForNameMatch(cu.charityId)
    const matchesAccountName =
      !!normalizedAccountName &&
      (normalizedFullName.includes(normalizedAccountName) || normalizedAccountName.includes(normalizedFullName))
    const matchesCharityId = !!normalizedCharityId && normalizeForNameMatch(lastName) === normalizedCharityId
    return matchesAccountName || matchesCharityId
  })
}

export function needsCustodianSettings(existingSettings) {
  // updateAdvisorCustodianSettings always overwrites the full object per its DTO, so a
  // saved settings object only ever exists once every required field has been submitted.
  return !existingSettings
}

/**
 * Runs the queries and completeness checks shared by both advisor-connection acceptance
 * pages (AcceptConnection, AcceptConnectionById) to decide whether AdvisorDetailsForm needs
 * to be shown before the connection can be accepted.
 */
export function useAdvisorAcceptanceGate({ skip = false } = {}) {
  const { data: meData, loading: meLoading } = useQuery(queries.user.me, { skip })
  const me = meData?.me

  const { data: settingsData, loading: settingsLoading } = useQuery(queries.user.myAdvisorCustodianSettings, { skip })
  const existingSettings = settingsData?.myAdvisorCustodianSettings

  const { data: suggestedData, loading: suggestedLoading } = useQuery(queries.user.suggestedCustodianName, {
    skip: skip || settingsLoading || !!existingSettings,
  })

  return {
    me,
    loading: meLoading || settingsLoading || suggestedLoading,
    formRequired: needsNameCollection(me) || needsCustodianSettings(existingSettings),
    advisorFundProfileId: me?.fundProfiles?.find((p) => p.fundType === 'advisor' && !p.closed)?.id,
    existingSettings,
    suggestedCustodianName: suggestedData?.suggestedCustodianName,
  }
}

/**
 * Collects the advisor's name and custodian (securities firm) settings before a connection
 * is accepted, since accepting without this data leaves the securities-contribution flow
 * unable to pre-fill it later. Mirrors the field set/validation rules of
 * webapp/src/pages/profile/custodian-settings.jsx.
 */
export function AdvisorDetailsForm({ me, existingSettings, suggestedCustodianName, advisorFundProfileId, onSave }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      advisorName: '',
      custodianName: '',
      advisorEmail: '',
      advisorPhone: '',
      advisorDealerRepCode: '',
      address: { lineOne: '', city: '', province: '', postalCode: '' },
    },
  })

  const hasPrefilledRef = useRef(false)

  useEffect(() => {
    // Guard against re-firing after the user has started typing - e.g. suggestedCustodianName
    // resolves asynchronously after this component first mounts, which would otherwise wipe
    // out anything already entered in fields with no other fallback (custodianName,
    // advisorDealerRepCode, address).
    if (hasPrefilledRef.current) return
    if (!me) return
    hasPrefilledRef.current = true

    const advisorName =
      existingSettings?.advisorName || me.name || `${me.profile?.firstName || ''} ${me.profile?.lastName || ''}`.trim()
    reset({
      advisorName: advisorName || '',
      custodianName: existingSettings?.custodianName || suggestedCustodianName || '',
      advisorEmail: existingSettings?.advisorEmail || me.email || '',
      advisorPhone: existingSettings?.advisorPhone || me.profile?.phone || '',
      advisorDealerRepCode: existingSettings?.advisorDealerRepCode || '',
      address: {
        lineOne: existingSettings?.address?.lineOne || '',
        city: existingSettings?.address?.city || '',
        province: existingSettings?.address?.province || '',
        postalCode: existingSettings?.address?.postalCode || '',
      },
    })
  }, [me, existingSettings, suggestedCustodianName, reset])

  const [updateProfile] = useMutation(mutations.user.updateProfile)
  const [updateMyAdvisorPortalName] = useMutation(mutations.user.updateMyAdvisorPortalName)
  const [updateAdvisorCustodianSettings] = useMutation(mutations.user.updateAdvisorCustodianSettings, {
    // AdvisorCustodianSettingsEntity has no id field, so Apollo can't normalize/patch the
    // myAdvisorCustodianSettings cache entry automatically - refetch it explicitly, same as
    // webapp/src/pages/profile/custodian-settings.jsx already does.
    refetchQueries: [{ query: queries.user.myAdvisorCustodianSettings }],
  })

  const onSubmit = async (values) => {
    const { advisorName } = values

    const mutationsToRun = [
      updateMyAdvisorPortalName({ variables: { fundProfileId: advisorFundProfileId, portalName: advisorName } }),
      updateAdvisorCustodianSettings({
        variables: {
          data: {
            custodianName: values.custodianName,
            advisorName,
            advisorEmail: values.advisorEmail,
            advisorPhone: values.advisorPhone,
            advisorDealerRepCode: values.advisorDealerRepCode || undefined,
            address: {
              lineOne: values.address.lineOne,
              city: values.address.city,
              province: values.address.province,
              postalCode: values.address.postalCode,
            },
          },
        },
      }),
    ]

    // Only overwrite the profile name from the advisor display name when the profile
    // doesn't already have a real one - never clobber an existing, correct name.
    if (needsNameCollection(me)) {
      const [firstName, ...rest] = advisorName.trim().split(/\s+/)
      mutationsToRun.push(updateProfile({ variables: { data: { firstName, lastName: rest.join(' ') } } }))
    }

    await Promise.all(mutationsToRun)
    onSave()
  }

  return (
    <CardBox sx={{ maxWidth: 560, mx: 'auto', mt: 8 }}>
      <Stack spacing={3}>
        <Text.H4>Just about done!</Text.H4>
        <Text.Body>We just need a few more details to finish setting things up. It only takes a minute.</Text.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Text.H6>Securities Firm Information</Text.H6>

            <Fields.Text
              label="Name of Securities Firm *"
              error={!!errors.custodianName}
              helperText={errors.custodianName?.message}
              {...register('custodianName', {
                required: 'This field is required',
                validate: {
                  noSpecialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
                },
              })}
            />

            <Text.H6>Advisor Information</Text.H6>

            <Fields.Text
              label="Contact Name (Advisor) *"
              error={!!errors.advisorName}
              helperText={errors.advisorName?.message}
              {...register('advisorName', { ...validations.required })}
            />

            <Fields.Text
              label="Advisor Dealer Rep Code"
              error={!!errors.advisorDealerRepCode}
              helperText={errors.advisorDealerRepCode?.message}
              {...register('advisorDealerRepCode')}
            />

            <Fields.Text
              label="Advisor Email *"
              error={!!errors.advisorEmail}
              helperText={errors.advisorEmail?.message}
              {...register('advisorEmail', { ...validations.required, ...validations.email })}
            />

            <Fields.Text
              label="Advisor Phone Number *"
              error={!!errors.advisorPhone}
              helperText={errors.advisorPhone?.message}
              {...register('advisorPhone', { ...validations.required, ...validations.phoneNumber })}
            />

            <Text.H6>Custodian Address</Text.H6>

            <Fields.Text
              label="Securities Dealer/Custodian Address *"
              error={!!errors?.address?.lineOne}
              helperText={errors?.address?.lineOne?.message}
              {...register('address.lineOne', { ...validations.required })}
            />

            <Fields.Text
              label="City *"
              error={!!errors?.address?.city}
              helperText={errors?.address?.city?.message}
              {...register('address.city', { ...validations.required })}
            />

            <Row>
              <Fields.Text
                label="Province/State *"
                error={!!errors?.address?.province}
                helperText={errors?.address?.province?.message}
                {...register('address.province', { ...validations.required })}
              />
              <Fields.Text
                label="Postal Code/ZIP *"
                error={!!errors?.address?.postalCode}
                helperText={errors?.address?.postalCode?.message}
                {...register('address.postalCode', { ...validations.required })}
              />
            </Row>

            <Stack spacing={1} alignItems="flex-end" sx={{ mt: '2rem!important' }}>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Continue'}
              </Button>
              <Text.Caption sx={{ color: 'text.secondary' }}>
                You can edit this information anytime in your profile settings.
              </Text.Caption>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </CardBox>
  )
}
